(() => {
  'use strict';

  const config = window.DRDocks || {};
  const localeRoot = () => (config.root || (window.Shopify && Shopify.routes && Shopify.routes.root) || '/');
  const money = (cents) => new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: 'USD' }).format((Number(cents) || 0) / 100);
  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  async function jsonFetch(url, options = {}) {
    const response = await fetch(url, { credentials: 'same-origin', ...options });
    let body = null;
    try { body = await response.json(); } catch (_) { body = null; }
    if (!response.ok) {
      const message = body && (body.description || body.message) ? (body.description || body.message) : `Request failed (${response.status})`;
      throw new Error(message);
    }
    return body;
  }

  function escapeHTML(value) {
    const d = document.createElement('div');
    d.textContent = value == null ? '' : String(value);
    return d.innerHTML;
  }

  class DrDocksMobileNav {
    constructor() {
      this.panel = document.querySelector('[data-dd-mobile-panel]');
      this.open = document.querySelector('[data-dd-menu-open]');
      this.close = document.querySelector('[data-dd-menu-close]');
      if (!this.panel || !this.open) return;
      this.open.addEventListener('click', () => this.toggle(true));
      this.close?.addEventListener('click', () => this.toggle(false));
      document.addEventListener('keydown', (event) => { if (event.key === 'Escape') this.toggle(false); });
    }
    toggle(on) {
      this.panel.classList.toggle('is-open', on);
      this.panel.setAttribute('aria-hidden', String(!on));
      this.open.setAttribute('aria-expanded', String(on));
      document.documentElement.classList.toggle('dd-menu-open', on);
      if (on) this.close?.focus(); else this.open?.focus();
    }
  }

  class DrDocksQty {
    constructor(el) {
      this.input = el.querySelector('input');
      el.querySelector('[data-minus]')?.addEventListener('click', () => this.set(-1));
      el.querySelector('[data-plus]')?.addEventListener('click', () => this.set(1));
    }
    set(delta) {
      if (!this.input) return;
      const min = Math.max(1, Number(this.input.min || 1));
      this.input.value = Math.max(min, Number(this.input.value || min) + delta);
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  class DrDocksProduct {
    constructor(el) {
      this.el = el;
      this.main = el.querySelector('[data-dd-gallery-main] img');
      this.thumbs = [...el.querySelectorAll('[data-dd-gallery-thumb]')];
      this.select = el.querySelector('[data-dd-variant-select]');
      this.hidden = el.querySelector('[data-dd-variant-id]');
      this.price = el.querySelector('[data-dd-product-price]');
      this.add = el.querySelector('[data-dd-add]');
      this.thumbs.forEach((thumb) => thumb.addEventListener('click', () => this.gallery(thumb)));
      this.select?.addEventListener('change', () => this.variant());
    }
    gallery(thumb) {
      if (this.main && thumb.dataset.image) {
        this.main.src = thumb.dataset.image;
        this.main.removeAttribute('srcset');
      }
      this.thumbs.forEach((item) => item.classList.toggle('is-active', item === thumb));
    }
    variant() {
      const option = this.select?.options[this.select.selectedIndex];
      if (!option) return;
      if (this.hidden) this.hidden.value = option.value;
      if (this.price) this.price.textContent = option.dataset.price || '';
      const available = option.dataset.available === 'true';
      if (this.add) {
        this.add.disabled = !available;
        const span = this.add.querySelector('span');
        if (span) span.textContent = available ? (config.strings?.addToCart || 'Add to cart →') : (config.strings?.unavailable || 'Unavailable');
      }
    }
  }

  class DrDocksFloatCalc {
    constructor(el) {
      this.el = el;
      this.inputs = [...el.querySelectorAll('input,select')];
      this.out = el.querySelector('[data-dd-floats]');
      this.buoy = el.querySelector('[data-dd-buoyancy]');
      this.required = el.querySelector('[data-dd-required]');
      this.label = el.querySelector('[data-dd-float-label]');
      this.link = el.querySelector('[data-dd-float-link]');
      this.increment = Math.max(1, Number(el.dataset.increment || 1));
      this.inputs.forEach((input) => input.addEventListener('input', () => this.calc()));
      this.calc();
    }
    calc() {
      const structure = Math.max(0, Number(this.el.querySelector('[name=structure_weight]')?.value || 0));
      const live = Math.max(0, Number(this.el.querySelector('[name=live_load]')?.value || 0));
      const factor = Math.max(1, Number(this.el.querySelector('[name=safety]')?.value || 1.5));
      const select = this.el.querySelector('[data-dd-float-option]');
      const option = select?.options[select.selectedIndex];
      const rating = Math.max(1, Number(option?.value || 600));
      const needed = (structure + live) * factor;
      let count = Math.max(1, Math.ceil(needed / rating));
      count = Math.ceil(count / this.increment) * this.increment;
      if (this.out) this.out.textContent = count;
      if (this.required) this.required.textContent = `${Math.round(needed).toLocaleString()} lb`;
      if (this.buoy) this.buoy.textContent = `${Math.round(count * rating).toLocaleString()} lb`;
      if (this.label) this.label.textContent = option?.dataset.label || `${rating} lb float`;
      if (this.link && option?.dataset.url) this.link.href = option.dataset.url;
    }
  }

  class DrDocksFinder {
    constructor(el) {
      this.el = el;
      this.options = [...el.querySelectorAll('[data-dd-option]')];
      this.result = el.querySelector('[data-dd-result]');
      this.label = el.querySelector('[data-dd-result-label]');
      this.options.forEach((option) => option.addEventListener('click', () => this.choose(option)));
    }
    choose(option) {
      this.options.forEach((item) => item.classList.toggle('is-selected', item === option));
      this.result?.classList.add('is-visible');
      if (this.label) this.label.textContent = option.dataset.result || 'Compare verified product dimensions';
    }
  }

  class DrDocksQuote {
    constructor(el) {
      this.el = el;
      this.form = el.querySelector('form');
      this.panels = [...el.querySelectorAll('[data-dd-quote-panel]')];
      this.nav = [...el.querySelectorAll('[data-dd-step-nav]')];
      this.next = el.querySelector('[data-dd-quote-next]');
      this.prev = el.querySelector('[data-dd-quote-prev]');
      this.submit = el.querySelector('[data-dd-quote-submit]');
      this.review = el.querySelector('[data-dd-quote-review]');
      this.step = 1;
      this.next?.addEventListener('click', () => this.go(this.step + 1));
      this.prev?.addEventListener('click', () => this.go(this.step - 1));
      this.nav.forEach((nav) => nav.addEventListener('click', () => {
        const target = Number(nav.dataset.ddStepNav);
        if (target < this.step) this.go(target);
      }));
      this.prefill();
      this.render();
    }
    prefill() {
      const params = new URLSearchParams(location.search);
      const destination = params.get('destination');
      const order = params.get('order');
      const products = params.get('products');
      const destinationField = this.el.querySelector('[data-dd-prefill=destination]');
      const orderField = this.el.querySelector('[data-dd-prefill=order]');
      const productsField = this.el.querySelector('[data-dd-prefill=products]');
      if (destination && destinationField) destinationField.value = destination;
      if (order && orderField) orderField.value = order;
      if (products && productsField) productsField.value = products;
    }
    valid() {
      const panel = this.panels[this.step - 1];
      if (!panel) return true;
      const required = [...panel.querySelectorAll('[required]')];
      for (const field of required) {
        if (!field.checkValidity()) { field.reportValidity(); return false; }
      }
      return true;
    }
    go(step) {
      if (step > this.step && !this.valid()) return;
      this.step = Math.max(1, Math.min(this.panels.length, step));
      if (this.step === this.panels.length) this.buildReview();
      this.render();
    }
    render() {
      this.panels.forEach((panel, index) => panel.classList.toggle('is-active', index === this.step - 1));
      this.nav.forEach((nav, index) => nav.classList.toggle('is-active', index === this.step - 1));
      if (this.prev) this.prev.hidden = this.step === 1;
      if (this.next) this.next.hidden = this.step === this.panels.length;
      if (this.submit) this.submit.hidden = this.step !== this.panels.length;
    }
    buildReview() {
      if (!this.review || !this.form) return;
      const rows = [];
      [...this.form.elements].forEach((field) => {
        if (!field.name || ['hidden','submit','button','checkbox'].includes(field.type) || !field.value) return;
        const label = field.closest('.dd-field')?.querySelector('label')?.textContent?.trim() || field.name.replace(/contact\[|\]/g, '');
        rows.push(`<div><span>${escapeHTML(label)}</span><strong>${escapeHTML(field.value)}</strong></div>`);
      });
      this.review.innerHTML = rows.join('');
    }
  }

  class DrDocksShippingRates {
    constructor(el) {
      this.el = el;
      this.form = el.querySelector('[data-dd-shipping-form]');
      this.country = el.querySelector('[data-dd-ship-country]');
      this.province = el.querySelector('[data-dd-ship-province]');
      this.zip = el.querySelector('[data-dd-ship-zip]');
      this.status = el.querySelector('[data-dd-ship-status]');
      this.results = el.querySelector('[data-dd-ship-results]');
      this.fallback = el.querySelector('[data-dd-ship-fallback]');
      this.quote = el.querySelector('[data-dd-ship-quote]');
      this.submit = el.querySelector('[data-dd-ship-submit]');
      this.root = el.dataset.root || localeRoot();
      this.subtotal = Number(el.dataset.subtotal || 0);
      this.quoteThreshold = Number(el.dataset.quoteThreshold || config.quoteThreshold || 1000000);
      this.forceQuoteFlag = el.dataset.forceQuote === 'true';
      this.form?.addEventListener('submit', (event) => { event.preventDefault(); this.calculate(); });
      this.restoreDestination();
      this.updateQuoteLink();
      if (this.subtotal >= this.quoteThreshold || this.forceQuoteFlag) this.forceQuote();
    }
    addressParams() {
      const params = new URLSearchParams();
      params.set('shipping_address[country]', this.country?.value || '');
      if (this.province?.value) params.set('shipping_address[province]', this.province.value.trim());
      if (this.zip?.value) params.set('shipping_address[zip]', this.zip.value.trim());
      return params;
    }
    restoreDestination() {
      try {
        const saved = JSON.parse(localStorage.getItem('dd_ship_destination') || '{}');
        if (saved.country && this.country && [...this.country.options].some((o) => o.value === saved.country)) this.country.value = saved.country;
        if (saved.province && this.province) this.province.value = saved.province;
        if (saved.zip && this.zip) this.zip.value = saved.zip;
      } catch (_) {}
    }
    saveDestination() {
      try { localStorage.setItem('dd_ship_destination', JSON.stringify({ country: this.country?.value || '', province: this.province?.value || '', zip: this.zip?.value || '' })); } catch (_) {}
    }
    updateQuoteLink() {
      if (!this.quote) return;
      const url = new URL(config.quoteUrl || this.quote.href, location.origin);
      if (this.country?.value) url.searchParams.set('destination', this.country.value);
      if (this.subtotal) url.searchParams.set('order', String(Math.round(this.subtotal / 100)));
      this.quote.href = url.pathname + url.search;
    }
    setStatus(message, type = '') {
      if (!this.status) return;
      this.status.textContent = message;
      this.status.dataset.state = type;
    }
    forceQuote() {
      this.form?.setAttribute('hidden', 'hidden');
      this.results && (this.results.innerHTML = '');
      if (this.fallback) this.fallback.hidden = false;
      this.setStatus(config.strings?.projectQuoteRequired || 'This order requires a custom project / freight quote.', 'quote');
    }
    async calculate() {
      if (!this.form || !this.country) return;
      if (this.subtotal >= this.quoteThreshold || this.forceQuoteFlag) return this.forceQuote();
      this.saveDestination();
      this.updateQuoteLink();
      this.fallback && (this.fallback.hidden = true);
      this.results && (this.results.innerHTML = '');
      this.submit && (this.submit.disabled = true);
      this.setStatus(config.strings?.calculating || 'Calculating live shipping rates…', 'loading');
      const params = this.addressParams();
      const root = this.root.endsWith('/') ? this.root : `${this.root}/`;
      try {
        await jsonFetch(`${root}cart/prepare_shipping_rates.json?${params.toString()}`, { method: 'POST' });
        let payload = null;
        for (let attempt = 0; attempt < 12; attempt += 1) {
          await sleep(attempt < 3 ? 450 : 800);
          const response = await fetch(`${root}cart/async_shipping_rates.json?${params.toString()}`, { credentials: 'same-origin' });
          if (!response.ok) throw new Error(`Shipping rate request failed (${response.status})`);
          payload = await response.json();
          if (payload && Array.isArray(payload.shipping_rates)) break;
        }
        const rates = payload?.shipping_rates || [];
        if (!rates.length) return this.noRates();
        this.renderRates(rates);
        this.setStatus(config.strings?.liveRatesFound || 'Live rates returned by Shopify.', 'success');
      } catch (error) {
        console.warn('[DR. DOCKS] Shipping rates:', error);
        this.noRates();
      } finally {
        if (this.submit) this.submit.disabled = false;
      }
    }
    noRates() {
      this.setStatus(config.strings?.noLiveRate || 'No verified live rate was returned for this shipment.', 'quote');
      if (this.fallback) this.fallback.hidden = false;
    }
    renderRates(rates) {
      if (!this.results) return;
      const normalized = rates.map((rate) => ({
        ...rate,
        numericPrice: Math.round(Number(rate.price || 0) * 100),
        maxDays: Array.isArray(rate.delivery_days) && rate.delivery_days.length ? Math.max(...rate.delivery_days.map(Number)) : null
      }));
      const byPrice = [...normalized].sort((a,b) => a.numericPrice - b.numericPrice);
      const selected = [];
      if (byPrice[0]) selected.push({ ...byPrice[0], badge: config.strings?.bestValue || 'Best value' });
      const withDays = normalized.filter((r) => Number.isFinite(r.maxDays)).sort((a,b) => a.maxDays - b.maxDays || a.numericPrice - b.numericPrice);
      const fastest = withDays[0];
      if (fastest && !selected.some((r) => r.code === fastest.code && r.numericPrice === fastest.numericPrice)) selected.push({ ...fastest, badge: config.strings?.fastest || 'Fastest' });
      const extra = byPrice.find((r) => !selected.some((s) => s.code === r.code && s.numericPrice === r.numericPrice));
      if (extra && selected.length < 3) selected.push({ ...extra, badge: '' });
      this.results.innerHTML = selected.map((rate, index) => {
        const delivery = rate.delivery_date ? `${config.strings?.estimatedDelivery || 'Estimated delivery'} ${escapeHTML(rate.delivery_date)}` : (rate.maxDays != null ? `${rate.maxDays} ${config.strings?.businessDays || 'business days'}` : (rate.description || ''));
        return `<label class="dd-rate-card ${index === 0 ? 'is-selected' : ''}"><input type="radio" name="dd_shipping_rate" ${index === 0 ? 'checked' : ''}><span class="dd-rate-card__main"><span class="dd-rate-card__title">${escapeHTML(rate.presentment_name || rate.name || rate.code || 'Shipping')}</span>${rate.badge ? `<span class="dd-rate-badge">${escapeHTML(rate.badge)}</span>` : ''}<small>${escapeHTML(delivery)}</small></span><strong>${money(rate.numericPrice)}</strong></label>`;
      }).join('') + `<div class="dd-rate-actions"><a class="dd-btn dd-btn--gold" data-dd-direct-checkout href="${escapeHTML(location.pathname.includes('/cart') ? (config.checkoutUrl || '/checkout') : (config.cartUrl || '/cart'))}">${escapeHTML(config.strings?.continueCheckout || 'Continue to checkout')} →</a><a class="dd-card-link" href="${escapeHTML(config.shippingUrl || '#')}">${escapeHTML(config.strings?.shippingDetails || 'Shipping details')}</a></div>`;
      this.results.querySelectorAll('.dd-rate-card').forEach((card) => card.addEventListener('click', () => {
        this.results.querySelectorAll('.dd-rate-card').forEach((c) => c.classList.toggle('is-selected', c === card));
      }));
    }
  }

  class DrDocksFeeManager {
    constructor() {
      this.handle = config.feeHandle;
      this.busy = false;
      if (!this.handle) return;
      this.sync().catch(() => {});
      document.addEventListener('cart:updated', () => this.sync().catch(() => {}));
    }
    async cart() { return jsonFetch(`${localeRoot()}cart.js`); }
    feeLine(cart) { return cart.items.find((item) => item.handle === this.handle || item.properties?._dd_system_fee === 'true'); }
    merchandiseItems(cart) { return cart.items.filter((item) => item.handle !== this.handle && item.properties?._dd_system_fee !== 'true'); }
    merchandiseSubtotal(cart) { return this.merchandiseItems(cart).reduce((sum, item) => sum + Number(item.final_line_price || 0), 0); }
    merchandiseUnits(cart) { return this.merchandiseItems(cart).reduce((sum, item) => sum + Number(item.quantity || 0), 0); }
    targetTier(subtotal) {
      if (subtotal <= 0 || subtotal >= Number(config.quoteThreshold || 1000000)) return null;
      if (subtotal < 100000) return 'tier1';
      if (subtotal < 500000) return 'tier2';
      return 'tier3';
    }
    async feeProduct() {
      if (this._feeProduct) return this._feeProduct;
      this._feeProduct = await jsonFetch(`${localeRoot()}products/${encodeURIComponent(this.handle)}.js`);
      return this._feeProduct;
    }
    variantFor(product, tier) {
      const expected = Number(config[`fee_${tier}`] || 0);
      const byPrice = product.variants?.find((variant) => Number(variant.price) === expected);
      if (byPrice) return byPrice;
      const words = { tier1: 'under', tier2: '1,000', tier3: '5,000' };
      return product.variants?.find((variant) => String(variant.title).toLowerCase().includes(words[tier])) || null;
    }
    async remove(line) {
      if (!line) return;
      await jsonFetch(`${localeRoot()}cart/change.js`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: line.key, quantity: 0 }) });
    }
    async add(variant) {
      await jsonFetch(`${localeRoot()}cart/add.js`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: [{ id: variant.id, quantity: 1, properties: { _dd_system_fee: 'true' } }] }) });
    }
    setQuoteRequired(required) {
      document.documentElement.classList.toggle('dd-quote-required', required);
      document.querySelectorAll('[data-dd-quote-required-message]').forEach((el) => { el.hidden = !required; });
      document.querySelectorAll('[name="checkout"], [data-dd-direct-checkout]').forEach((button) => { button.disabled = required; button.setAttribute('aria-disabled', String(required)); });
    }
    async sync() {
      if (this.busy) return;
      this.busy = true;
      try {
        const cart = await this.cart();
        const subtotal = this.merchandiseSubtotal(cart);
        const line = this.feeLine(cart);
        const units = this.merchandiseUnits(cart);
        const highValueNeedsProtection = !config.cargoProtectionLive && subtotal >= Number(config.protectionThreshold || 150000);
        const quoteRequired = !config.packageSetupComplete || subtotal >= Number(config.quoteThreshold || 1000000) || units > Number(config.directUnitsMax || 1) || highValueNeedsProtection;
        this.setQuoteRequired(quoteRequired);
        const tier = this.targetTier(subtotal);
        if (!tier) {
          if (line) { await this.remove(line); location.reload(); }
          return;
        }
        const product = await this.feeProduct();
        const targetVariant = this.variantFor(product, tier);
        if (!targetVariant) return;
        if (!line || Number(line.variant_id) !== Number(targetVariant.id) || Number(line.quantity) !== 1) {
          if (line) await this.remove(line);
          await this.add(targetVariant);
          location.reload();
        }
      } catch (error) {
        console.info('[DR. DOCKS] Logistics fee not synchronized yet:', error.message);
      } finally { this.busy = false; }
    }
  }

  const SavedStore = {
    key(type) { return `dd_${type}_handles`; },
    get(type) { try { const v = JSON.parse(localStorage.getItem(this.key(type)) || '[]'); return Array.isArray(v) ? [...new Set(v)].slice(0, type === 'compare' ? 4 : 50) : []; } catch (_) { return []; } },
    set(type, handles) { try { localStorage.setItem(this.key(type), JSON.stringify([...new Set(handles)])); } catch (_) {} },
    toggle(type, handle) { const list = this.get(type); const exists = list.includes(handle); const next = exists ? list.filter((h) => h !== handle) : [...list, handle]; this.set(type, next); return !exists; },
    remove(type, handle) { this.set(type, this.get(type).filter((h) => h !== handle)); }
  };

  class DrDocksSaveButtons {
    constructor() {
      this.buttons = [...document.querySelectorAll('[data-dd-save]')];
      this.compareButtons = [...document.querySelectorAll('[data-dd-compare]')];
      this.buttons.forEach((button) => this.bind(button, 'saved'));
      this.compareButtons.forEach((button) => this.bind(button, 'compare'));
      this.refresh();
    }
    bind(button, type) {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        const handle = button.dataset.handle;
        if (!handle) return;
        const added = SavedStore.toggle(type, handle);
        if (type === 'compare' && added && SavedStore.get('compare').length > 4) {
          const list = SavedStore.get('compare');
          SavedStore.set('compare', list.slice(-4));
        }
        this.refresh();
      });
    }
    refresh() {
      const saved = SavedStore.get('saved');
      const compare = SavedStore.get('compare');
      this.buttons.forEach((button) => { const on = saved.includes(button.dataset.handle); button.classList.toggle('is-active', on); button.setAttribute('aria-pressed', String(on)); });
      this.compareButtons.forEach((button) => { const on = compare.includes(button.dataset.handle); button.classList.toggle('is-active', on); button.setAttribute('aria-pressed', String(on)); });
      document.querySelectorAll('[data-dd-saved-count]').forEach((el) => { el.textContent = saved.length; });
      document.querySelectorAll('[data-dd-compare-count]').forEach((el) => { el.textContent = compare.length; });
    }
  }

  class DrDocksSavedPage {
    constructor(el) {
      this.el = el;
      this.type = el.dataset.ddSavedPage || 'saved';
      this.grid = el.querySelector('[data-dd-saved-grid]');
      this.empty = el.querySelector('[data-dd-saved-empty]');
      this.render();
    }
    async product(handle) {
      try { return await jsonFetch(`${localeRoot()}products/${encodeURIComponent(handle)}.js`); } catch (_) { return null; }
    }
    async render() {
      const handles = SavedStore.get(this.type);
      if (!handles.length) { if (this.empty) this.empty.hidden = false; if (this.grid) this.grid.innerHTML = ''; return; }
      if (this.empty) this.empty.hidden = true;
      const products = (await Promise.all(handles.map((handle) => this.product(handle)))).filter(Boolean);
      if (!this.grid) return;
      this.grid.innerHTML = products.map((product) => this.card(product)).join('');
      this.grid.querySelectorAll('[data-dd-remove-saved]').forEach((button) => button.addEventListener('click', () => { SavedStore.remove(this.type, button.dataset.handle); this.render(); }));
    }
    card(product) {
      const image = product.featured_image || product.images?.[0] || '';
      const specs = this.type === 'compare' ? `<dl class="dd-compare-specs"><div><dt>${escapeHTML(config.strings?.price || 'Price')}</dt><dd>${money(product.price)}</dd></div><div><dt>${escapeHTML(config.strings?.vendor || 'Manufacturer')}</dt><dd>${escapeHTML(product.vendor || 'Taylor Made')}</dd></div><div><dt>${escapeHTML(config.strings?.type || 'Type')}</dt><dd>${escapeHTML(product.type || '—')}</dd></div></dl>` : '';
      return `<article class="dd-card dd-saved-card">${image ? `<a href="${escapeHTML(product.url)}"><img src="${escapeHTML(image)}" alt="${escapeHTML(product.title)}" loading="lazy"></a>` : '<div class="dd-product-placeholder">Product image</div>'}<div class="dd-saved-card__body"><p class="dd-kicker">${escapeHTML(product.vendor || 'Taylor Made')}</p><h3><a href="${escapeHTML(product.url)}">${escapeHTML(product.title)}</a></h3><strong class="dd-saved-price">${money(product.price)}</strong>${specs}<div class="dd-saved-card__actions"><a class="dd-btn" href="${escapeHTML(product.url)}">${escapeHTML(config.strings?.viewProduct || 'View product')} →</a><button class="dd-text-button" type="button" data-dd-remove-saved data-handle="${escapeHTML(product.handle)}">${escapeHTML(config.strings?.remove || 'Remove')}</button></div></div></article>`;
    }
  }


  class DrDocksInsuranceAck {
    constructor() {
      this.checkbox = document.querySelector('[data-dd-uninsured-ack]');
      if (!this.checkbox) return;
      this.checkbox.addEventListener('change', () => this.update());
      this.update();
      document.addEventListener('cart:updated', () => this.update());
    }
    update() {
      const required = this.checkbox && !this.checkbox.checked;
      document.documentElement.classList.toggle('dd-insurance-ack-required', Boolean(required));
      document.querySelectorAll('[name="checkout"], [data-dd-direct-checkout]').forEach((control) => {
        const quoteRequired = document.documentElement.classList.contains('dd-quote-required');
        const disabled = Boolean(required || quoteRequired);
        if ('disabled' in control) control.disabled = disabled;
        control.setAttribute('aria-disabled', String(disabled));
        if (control.tagName === 'A') {
          if (disabled) control.setAttribute('tabindex','-1'); else control.removeAttribute('tabindex');
        }
      });
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    new DrDocksMobileNav();
    document.querySelectorAll('[data-dd-qty]').forEach((el) => new DrDocksQty(el));
    document.querySelectorAll('[data-dd-product]').forEach((el) => new DrDocksProduct(el));
    document.querySelectorAll('[data-dd-float-calc]').forEach((el) => new DrDocksFloatCalc(el));
    document.querySelectorAll('[data-dd-finder]').forEach((el) => new DrDocksFinder(el));
    document.querySelectorAll('[data-dd-quote]').forEach((el) => new DrDocksQuote(el));
    document.querySelectorAll('[data-dd-shipping-rates]').forEach((el) => new DrDocksShippingRates(el));
    document.querySelectorAll('[data-dd-saved-page]').forEach((el) => new DrDocksSavedPage(el));
    new DrDocksSaveButtons();
    new DrDocksFeeManager();
    new DrDocksInsuranceAck();
    document.addEventListener('click', (event) => { const a = event.target.closest('a[aria-disabled="true"]'); if (a) event.preventDefault(); });
    document.querySelector('[data-dd-filter-toggle]')?.addEventListener('click', () => document.querySelector('[data-dd-filter-panel]')?.classList.toggle('is-open'));
  });
})();
